import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { withZod } from '@remix-validated-form/with-zod'
import { useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import { Button as UiButton } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import type { LocationJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
})

export const createClickFunnelsValidator = withZod(createSchema)

type ClickFunnelsWorkspace = { id: string; public_id?: string; name: string; subdomain?: string }
type ClickFunnelsTeam = { id: string; name: string; workspaces: ClickFunnelsWorkspace[] }

export function ClickFunnelsFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    clickFunnelsConfig?: { subdomain?: string; workspaceId?: string; apiKeySet?: boolean }
    webhookUrl?: string
  }
}) {
  const [location, setLocation] = useState<OptionType | undefined>(
    currentValues
      ? { id: currentValues.locationId, name: currentValues.locationName, value: currentValues.locationId }
      : undefined,
  )
  const [confirm] = useConfirmation()
  const [subdomain, setSubdomain] = useState(``)
  const [apiKey, setApiKey] = useState(``)
  const [teams, setTeams] = useState<ClickFunnelsTeam[] | undefined>(undefined)
  const [selectedWorkspace, setSelectedWorkspace] = useState<ClickFunnelsWorkspace | undefined>(undefined)
  const [discovering, setDiscovering] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [removing, setRemoving] = useState(false)

  const connected = !!currentValues?.clickFunnelsConfig

  const onDiscover = () => {
    if (!subdomain || !apiKey) return
    setDiscovering(true)
    setTeams(undefined)
    setSelectedWorkspace(undefined)
    window
      .fetch(`/api/integrations/clickfunnels/workspaces`, {
        method: `POST`,
        credentials: `include`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({ subdomain, apiKey }),
      })
      .then((res) => res.json())
      .then((data: { teams?: ClickFunnelsTeam[]; error?: string }) => {
        if (data?.teams) {
          setTeams(data.teams)
        } else {
          notifyError(`Failed to discover workspaces: ${data?.error || `Unknown error`}`)
        }
      })
      .catch(() => notifyError(`Failed to discover workspaces`))
      .finally(() => setDiscovering(false))
  }

  const onConnect = () => {
    const locationId = currentValues?.locationId || (location?.value as string)
    if (!locationId || !subdomain || !apiKey || !selectedWorkspace) return
    setConnecting(true)
    window
      .fetch(`/api/integrations/setup/clickfunnels`, {
        method: `POST`,
        credentials: `include`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({ locationId, subdomain, workspaceId: selectedWorkspace.id, apiKey }),
      })
      .then((res) => res.json())
      .then((data: { success?: boolean; error?: string }) => {
        if (data?.success) {
          notifySuccess(`ClickFunnels integration saved`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to save integration: ${data?.error || `Unknown error`}`)
        }
      })
      .catch(() => notifyError(`Failed to save integration`))
      .finally(() => setConnecting(false))
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove ClickFunnels Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() => {
      setRemoving(true)
      return window
        .fetch(`/api/integrations/remove`, {
          method: `POST`,
          credentials: `include`,
          headers: { 'Content-Type': `application/json` },
          body: JSON.stringify({ locationId: currentValues?.locationId, integration: `clickfunnels` }),
        })
        .then((res) => res.json())
        .then((data: { success?: boolean; error?: string }) => {
          if (data?.success) {
            notifySuccess(`ClickFunnels integration removed`)
            onAdd()
            closeModal()
          } else {
            notifyError(`Failed to remove integration: ${data?.error || `Unknown error`}`)
          }
        })
        .catch(() => notifyError(`Failed to remove integration`))
        .finally(() => setRemoving(false))
    })
  }

  const allWorkspaces = teams?.flatMap((team) => team.workspaces.map((ws) => ({ team, ws }))) || []

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-150 grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createClickFunnelsValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={() => {}}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={6}
            name="locationId"
            label="Trackable Location"
            labelClassName="text-body-sm text-strong"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
          />
        ) : (
          <FormComboAsync
            cols={6}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={location}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.clickFunnelsKey,
                  tooltipForDisabledOption: `This location is already connected to ClickFunnels`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}

        {connected ? (
          <div className="col-span-6 grid grid-cols-6 gap-y-3">
            <div className="col-span-6 rounded-md border border-default bg-surface-subtle px-3 py-2">
              <p className="text-body-sm text-subtle">
                Subdomain:{` `}
                <span className="font-medium text-strong">{currentValues?.clickFunnelsConfig?.subdomain}</span>
              </p>
              <p className="text-body-sm text-subtle">
                Workspace ID:{` `}
                <span className="font-medium text-strong">{currentValues?.clickFunnelsConfig?.workspaceId}</span>
              </p>
            </div>
            {currentValues?.webhookUrl && (
              <div className="col-span-6">
                <Label className="text-body-sm font-normal text-strong">Webhook URL</Label>
                <div className="mt-1 flex items-center gap-2 rounded-md border border-default bg-surface-subtle px-3 py-2">
                  <code className="flex-1 break-all text-xs text-default">{currentValues.webhookUrl}</code>
                  <CopyToClipboard text={currentValues.webhookUrl} />
                </div>
                <p className="mt-1 text-xs text-subtle">
                  Use this URL for manual setup of the ClickFunnels form submission webhook.
                </p>
              </div>
            )}
          </div>
        ) : (
          location && (
            <div className="col-span-6 grid grid-cols-6 gap-x-4 gap-y-3">
              {/* new-password keeps Chrome from filling a saved login into this pair. */}
              <div className="col-span-3">
                <Label htmlFor="clickfunnels-subdomain" className="text-body-sm font-normal text-strong">
                  Subdomain
                </Label>
                <Input
                  id="clickfunnels-subdomain"
                  type="text"
                  size="lg"
                  wrapperClassName="mt-1"
                  autoComplete="off"
                  placeholder="mysite"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                />
              </div>
              <div className="col-span-3">
                <Label htmlFor="clickfunnels-api-key" className="text-body-sm font-normal text-strong">
                  API Key
                </Label>
                <Input
                  id="clickfunnels-api-key"
                  type="password"
                  size="lg"
                  wrapperClassName="mt-1"
                  autoComplete="new-password"
                  placeholder="ClickFunnels API key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>
              <div className="col-span-6">
                <Button
                  type="button"
                  label={discovering ? `Discovering...` : `Discover Workspaces`}
                  mode="secondary"
                  size="md"
                  disabled={!subdomain || !apiKey || discovering}
                  loading={discovering}
                  onClick={() => onDiscover()}
                />
              </div>

              {teams && (
                <div className="col-span-6">
                  <Label className="text-body-sm font-normal text-strong">Workspace</Label>
                  {allWorkspaces.length === 0 ? (
                    <div className="mt-1 text-body-sm text-subtle">No workspaces found</div>
                  ) : (
                    <div className="mt-1 flex flex-col gap-y-1">
                      {allWorkspaces.map(({ team, ws }) => (
                        <UiButton
                          type="button"
                          key={ws.id}
                          variant="toggle"
                          size="row"
                          className="px-3 py-2 text-body-sm text-strong"
                          aria-pressed={selectedWorkspace?.id === ws.id}
                          onClick={() => setSelectedWorkspace(ws)}
                        >
                          {ws.name}
                          <span className="text-subtle">({team.name})</span>
                        </UiButton>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? (
              <Button
                label={removing ? `Removing...` : `Remove`}
                mode="danger"
                size="md"
                disabled={removing}
                onClick={() => onRemove()}
              />
            ) : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Close" mode="white" size="md" onClick={() => closeModal()} />
            )}
            {!connected && (
              <Button
                type="button"
                label={connecting ? `Connecting...` : `Connect`}
                mode="primary"
                size="md"
                disabled={!location || !selectedWorkspace || connecting}
                loading={connecting}
                onClick={() => onConnect()}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
